import { useEffect, useState } from 'react';
import { graphService } from '../../shared/services/graph.service';
import { ResolvedScopeMember, ScopeBucket, ScopeMemberKind } from './scopedMembers';

// Entra group names used to arrive from a full /groups listing preloaded at boot, which
// paged the entire tenant directory before the app could render. Only the groups a record
// is actually shared with are ever displayed, so resolve those on demand instead.
//
// The cache is module level: one Available To column renders a cell per row, and the same
// group is usually shared to many of them.
const resolved = new Map<string, string>();
const inFlight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
const graphAPI = new graphService();

const fetchName = (groupId: string) => {
  if (resolved.has(groupId) || inFlight.has(groupId)) return;

  const request = graphAPI
    .getGroupById(groupId)
    .then(response => { resolved.set(groupId, response.data?.displayName ?? ''); })
    // A group the signed-in user cannot read is cached blank rather than retried on
    // every render.
    .catch(() => { resolved.set(groupId, ''); })
    .finally(() => {
      inFlight.delete(groupId);
      listeners.forEach(notify => notify());
    });

  inFlight.set(groupId, request);
};

const unresolvedIds = (members: ResolvedScopeMember[]): string[] =>
  members
    .filter(member =>
      member.bucket === ScopeBucket.Entra &&
      member.kind === ScopeMemberKind.Group &&
      member.aadObjectId &&
      !resolved.has(member.aadObjectId))
    .map(member => member.aadObjectId);

export const useEntraGroupNames = (members: ResolvedScopeMember[]): Map<string, string> => {
  const [, setTick] = useState(0);
  const pending = unresolvedIds(members).join(',');

  useEffect(() => {
    const rerender = () => setTick(tick => tick + 1);
    listeners.add(rerender);

    return () => { listeners.delete(rerender); };
  }, []);

  useEffect(() => {
    if (pending) pending.split(',').forEach(fetchName);
  }, [pending]);

  return resolved;
};
